import React, { useState, useEffect } from 'react';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Save, Loader2, Building2, User as UserIcon, ExternalLink, AlertCircle, Package, Plus } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TargetProductsTagInput from "@/components/ui/TargetProductsTagInput";

export default function QuickEditManager({ currentTenant: currentTenantProp }) {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const currentUser = contextUser;
  const effectiveTenant = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const [activeTab, setActiveTab] = useState('accounts');
  
  // Account state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountScore, setAccountScore] = useState('');
  const [accountRationale, setAccountRationale] = useState('');
  const [accountTier, setAccountTier] = useState('');
  const [incumbentSentiment, setIncumbentSentiment] = useState(''); // New state variable
  const [sentimentRationale, setSentimentRationale] = useState(''); // New state variable
  const [crmProduct, setCrmProduct] = useState('');
  const [eportfolioProduct, setEportfolioProduct] = useState('');
  const [proctoringProduct, setProctoringProduct] = useState('');
  const [libraryProduct, setLibraryProduct] = useState('');
  const [admissionsProduct, setAdmissionsProduct] = useState('');
  const [homeProduct, setHomeProduct] = useState('');
  const [fariaProducts, setFariaProducts] = useState('');
  const [renewalRisk, setRenewalRisk] = useState('');
  const [riskRationale, setRiskRationale] = useState('');
  const [expansionProduct1, setExpansionProduct1] = useState('');
  const [expansionProduct2, setExpansionProduct2] = useState('');
  const [expansionBadge1, setExpansionBadge1] = useState('');
  const [expansionBadge2, setExpansionBadge2] = useState('');
  const [expansionRationale1, setExpansionRationale1] = useState(''); 
  const [expansionRationale2, setExpansionRationale2] = useState(''); 
  const [targetProducts, setTargetProducts] = useState([]);
  const [showTargetProductsField, setShowTargetProductsField] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  
  // Persona state
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personSearch, setPersonSearch] = useState('');
  const [filterFormerCustomers, setFilterFormerCustomers] = useState(false);
  // Removed individual state variables for linkedinUrl, workedForCustomer, clientName
  // as they are now managed within selectedPerson via personFields
  const [isLoadingPeople, setIsLoadingPeople] = useState(false);
  const [isSavingPerson, setIsSavingPerson] = useState(false);
  
  const { toast } = useToast();

  const personFields = [
    { key: 'first_name', label: 'First Name', type: 'text', readOnly: true }, // Read-only in this form
    { key: 'last_name', label: 'Last Name', type: 'text', readOnly: true },  // Read-only in this form
    { key: 'role_title', label: 'Role Title', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'linkedin_url', label: 'LinkedIn URL', type: 'text' },
    { key: 'profile_photo_url', label: 'Profile Photo URL', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'location', label: 'Location', type: 'text' },
    { key: 'account_name', label: 'Account Name', type: 'text', readOnly: true }, // Read-only in this form
    { 
      key: 'department', 
      label: 'Department', 
      type: 'select',
      options: ['Finance', 'Executive', 'Marketing', 'Student Affairs', 'IT', 'Teaching & Learning', 'Operations', 'Procurement']
    },
    { 
      key: 'seniority', 
      label: 'Seniority', 
      type: 'select',
      options: ['Director', 'VP', 'C-Suite', 'Manager', 'Coordinator', 'Analyst']
    },
    { key: 'persona_fit_score', label: 'Persona Fit Score', type: 'number', min: 0, max: 100 },
    { key: 'persona_score_rationale', label: 'Score Rationale', type: 'textarea' },
    { key: 'procurement_influence_score', label: 'Procurement Influence', type: 'number', min: 0, max: 100 },
    { key: 'social_presence_score', label: 'Social Presence Score', type: 'number', min: 0, max: 100 },
    { key: 'worked_for_customer', label: 'Worked for Customer', type: 'boolean' },
    { key: 'client_name', label: 'Client Name', type: 'text' },
    { key: 'start_date', label: 'Start Date', type: 'date' },
    { key: 'add_to_pursuit', label: 'Add to Pursuit', type: 'boolean' },
    { key: 'is_visible', label: 'Is Visible', type: 'boolean' },
    { key: 'product_affinity', label: 'Product Affinity', type: 'product_affinity' },
  ];

  useEffect(() => {
    if (contextUser) {
      // Clear selections when tenant changes
      setSelectedAccount(null);
      setSelectedPerson(null);
      setAccounts([]);
      setPeople([]);
    }
  }, [currentTenant, contextUser]);

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const allAccounts = await AccountFinal.filter({}, '-account_fit_score', 500);
      setAccounts(allAccounts.filter(acc => acc.is_visible !== false));
    } catch (error) {
      console.error('Failed to load accounts:', error);
      toast({
        title: "Error",
        description: "Failed to load accounts",
        variant: "destructive"
      });
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const loadPeople = async () => {
    setIsLoadingPeople(true);
    try {
      const allPeople = await PersonFinal.filter({}, '-persona_fit_score', 2000);
      console.log('🔧 QuickEditManager loaded people:', {
        total: allPeople.length,
        prowise: allPeople.filter(p => p.tenant_id === 'prowise').length,
        sample: allPeople.slice(0, 3).map(p => ({ name: `${p.first_name} ${p.last_name}`, tenant_id: p.tenant_id }))
      });
      setPeople(allPeople.filter(p => p.is_visible !== false));
    } catch (error) {
      console.error('Failed to load people:', error);
      toast({
        title: "Error",
        description: "Failed to load people",
        variant: "destructive"
      });
    } finally {
      setIsLoadingPeople(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'accounts' && accounts.length === 0 && effectiveTenant) {
      loadAccounts();
    }
  }, [activeTab, accounts.length, effectiveTenant]); // Added accounts.length to deps

  useEffect(() => {
    if (activeTab === 'people' && people.length === 0 && effectiveTenant) {
      loadPeople();
    }
  }, [activeTab, people.length, effectiveTenant]); // Added people.length to deps

  const filteredAccounts = accounts.filter(acc =>
    acc.account_name?.toLowerCase().includes(accountSearch.toLowerCase())
  );

  const filteredPeople = people.filter(p => {
    const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
    const search = personSearch.toLowerCase();
    const matchesSearch = fullName.includes(search) || 
           p.account_name?.toLowerCase().includes(search) ||
           p.role_title?.toLowerCase().includes(search);
    
    // Check 'worked_for_customer' from the person object directly, as it's the base field.
    // The form will display the current value, which might be an override.
    const matchesFilter = !filterFormerCustomers || p.worked_for_customer === true;
    
    return matchesSearch && matchesFilter;
  });

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    
    // Check for tenant-specific override first
    const tenantOverride = account.tenant_overrides?.[effectiveTenant];
    const score = tenantOverride?.account_fit_score !== undefined 
      ? tenantOverride.account_fit_score 
      : account.account_fit_score;
    const rationale = tenantOverride?.score_rationale !== undefined
      ? tenantOverride.score_rationale
      : account.score_rationale;
    const sentiment = tenantOverride?.incumbent_sentiment !== undefined // New
      ? tenantOverride.incumbent_sentiment // New
      : account.incumbent_sentiment; // New
    const sentimentRat = tenantOverride?.incumbent_sentiment_rationale !== undefined // New
      ? tenantOverride.incumbent_sentiment_rationale // New
      : account.incumbent_sentiment_rationale; // New
    
    setAccountScore(score || '');
    setAccountRationale(rationale || '');
    setAccountTier(account.account_tier || '');
    setIncumbentSentiment(sentiment || ''); // New
    setSentimentRationale(sentimentRat || ''); // New
    
    // Tech stack products
    setCrmProduct(account.crm_vendor || '');
    setEportfolioProduct(account.eportfolio_vendor || '');
    setProctoringProduct(account.proctoring_vendor || '');
    setLibraryProduct(account.library_product || '');
    setAdmissionsProduct(account.admissions_product || '');
    setHomeProduct(account.home_product || '');
    
    // Faria-specific fields
    setFariaProducts(account.faria_products || '');
    setRenewalRisk(account.renewal_risk || '');
    setRiskRationale(account.risk_rationale || '');
    setExpansionProduct1(account.expansion_product_1 || '');
    setExpansionProduct2(account.expansion_product_2 || '');
    setExpansionBadge1(account.expansion_badge_1 || '');
    setExpansionBadge2(account.expansion_badge_2 || '');
    setExpansionRationale1(account.expansion_rationale_1 || ''); 
    setExpansionRationale2(account.expansion_rationale_2 || ''); 
    setTargetProducts(Array.isArray(account.target_products) ? [...account.target_products] : []);
    setShowTargetProductsField(Array.isArray(account.target_products) && account.target_products.length > 0);
  };

  const handleSelectPerson = (person) => {
    // Create a working copy of the person object, applying tenant overrides for display
    const effectivePerson = { ...person };
    const tenantOverride = person.tenant_overrides?.[effectiveTenant];

    if (tenantOverride) {
      personFields.forEach(field => {
        if (tenantOverride[field.key] !== undefined) {
          effectivePerson[field.key] = tenantOverride[field.key];
        }
      });
    }
    setSelectedPerson(effectivePerson);
  };

  const handlePersonFieldChange = (fieldKey, value) => {
    setSelectedPerson(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSaveAccount = async () => {
    if (!selectedAccount || !effectiveTenant) return;

    setIsSavingAccount(true);
    try {
      const isOwnTenant = selectedAccount.tenant_id === effectiveTenant;
      
      let updateData;
      if (isOwnTenant) {
        // Update base fields if it's the account's own tenant
        updateData = {
          account_fit_score: accountScore ? parseFloat(accountScore) : null,
          score_rationale: accountRationale || null,
          tier: accountTier || null,
          incumbent_sentiment: incumbentSentiment || null, // New
          incumbent_sentiment_rationale: sentimentRationale || null, // New
          crm_vendor: crmProduct || null,
          eportfolio_vendor: eportfolioProduct || null,
          proctoring_vendor: proctoringProduct || null,
          library_product: libraryProduct || null,
          admissions_product: admissionsProduct || null,
          home_product: homeProduct || null,
          faria_products: fariaProducts || null,
          renewal_risk: renewalRisk || null,
          risk_rationale: riskRationale || null,
          expansion_product_1: expansionProduct1 || null,
          expansion_product_2: expansionProduct2 || null,
          expansion_badge_1: expansionBadge1 || null,
          expansion_badge_2: expansionBadge2 || null,
          expansion_rationale_1: expansionRationale1 || null, 
          expansion_rationale_2: expansionRationale2 || null,
          target_products: targetProducts?.length > 0 ? targetProducts : null
        };
      } else {
        // Use tenant_overrides for other tenants
        const currentOverrides = selectedAccount.tenant_overrides || {};
        updateData = {
          tenant_overrides: {
            ...currentOverrides,
            [effectiveTenant]: {
              ...(currentOverrides[effectiveTenant] || {}),
              account_fit_score: accountScore ? parseFloat(accountScore) : null,
              score_rationale: accountRationale || null,
              incumbent_sentiment: incumbentSentiment || null, // New
              incumbent_sentiment_rationale: sentimentRationale || null // New
            }
          },
          // Tech stack and other fields are always saved to base fields (not tenant-specific)
          tier: accountTier || null,
          crm_vendor: crmProduct || null,
          eportfolio_vendor: eportfolioProduct || null,
          proctoring_vendor: proctoringProduct || null,
          library_product: libraryProduct || null,
          admissions_product: admissionsProduct || null,
          home_product: homeProduct || null,
          faria_products: fariaProducts || null,
          renewal_risk: renewalRisk || null,
          risk_rationale: riskRationale || null,
          expansion_product_1: expansionProduct1 || null,
          expansion_product_2: expansionProduct2 || null,
          expansion_badge_1: expansionBadge1 || null,
          expansion_badge_2: expansionBadge2 || null,
          expansion_rationale_1: expansionRationale1 || null, 
          expansion_rationale_2: expansionRationale2 || null,
          target_products: targetProducts?.length > 0 ? targetProducts : null
        };
      }

      const updatedAccount = await AccountFinal.update(selectedAccount.id, updateData);
      
      // Update local state, ensuring overrides are preserved for the next selection
      setAccounts(prev => prev.map(acc => 
        acc.id === selectedAccount.id 
          ? { ...acc, ...updatedAccount } // Use the actual updated object from the backend
          : acc
      ));
      
      setSelectedAccount(updatedAccount); // Also update selectedAccount with the full updated object

      toast({
        title: "Success",
        description: `Updated ${selectedAccount.account_name} for ${effectiveTenant}`,
      });
    } catch (error) {
      console.error('Failed to update account:', error);
      toast({
        title: "Error",
        description: "Failed to update account",
        variant: "destructive"
      });
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleSavePerson = async () => {
    if (!selectedPerson || !effectiveTenant) return;

    setIsSavingPerson(true);
    try {
      const isOwnTenant = selectedPerson.tenant_id === effectiveTenant;
      
      // Collect all field values from the current selectedPerson state
      const updatePayload = {};
      const fieldsToUpdateInBase = {}; // Fields that are always updated in base (e.g., worked_for_customer, client_name)
      const fieldsToUpdateAsOverride = {}; // Fields that can be overridden by tenant

      personFields.forEach(field => {
        // Exclude read-only fields from being part of the update payload
        if (field.readOnly) return;

        const value = selectedPerson[field.key];
        // Only include fields that have a value or explicitly false (for booleans), or product_affinity (allow empty to clear)
        if (value !== undefined && (value !== '' || field.type === 'boolean' || field.type === 'product_affinity')) {
          // Determine if the field should always update base or can be an override
          // For simplicity, let's assume 'worked_for_customer' and 'client_name' are base fields
          // and others can be overridden if not own tenant.
          // This logic can be refined based on specific field requirements.
          if (field.key === 'worked_for_customer' || field.key === 'client_name') {
            fieldsToUpdateInBase[field.key] = value;
          } else {
            fieldsToUpdateAsOverride[field.key] = field.key === 'product_affinity' ? (value || null) : value;
          }
        }
      });
      
      let finalUpdateData;
      if (isOwnTenant) {
        // Update base fields if it's the person's own tenant
        // In this case, all changes go to the base record
        finalUpdateData = { ...fieldsToUpdateInBase, ...fieldsToUpdateAsOverride };
      } else {
        // Use tenant_overrides for other tenants
        const currentOverrides = selectedPerson.tenant_overrides || {};
        finalUpdateData = {
          ...fieldsToUpdateInBase, // Base fields are always updated directly
          tenant_overrides: {
            ...currentOverrides,
            [effectiveTenant]: {
              ...(currentOverrides[effectiveTenant] || {}),
              ...fieldsToUpdateAsOverride // Override-eligible fields go into tenant_overrides
            }
          }
        };
      }

      const updatedPerson = await PersonFinal.update(selectedPerson.id, finalUpdateData);
      
      // Update local state, ensuring overrides are preserved for the next selection
      setPeople(prev => prev.map(p => 
        p.id === selectedPerson.id 
          ? { ...p, ...updatedPerson } // Use the actual updated object from the backend
          : p
      ));
      
      setSelectedPerson(updatedPerson); // Also update selectedPerson with the full updated object

      toast({
        title: "Success",
        description: `Updated ${selectedPerson.first_name} ${selectedPerson.last_name} for ${effectiveTenant}`,
      });
    } catch (error) {
      console.error('Failed to update person:', error);
      toast({
        title: "Error",
        description: "Failed to update person",
        variant: "destructive"
      });
    } finally {
      setIsSavingPerson(false);
    }
  };

  // Only show to admin users
  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  const getTenantLabel = (tenantId) => {
    const labels = {
      'instructure': 'Instructure',
      'clairelabs': 'Claire Labs',
      'demarque': 'Demarque',
      'demarque_V2': 'Demarque V2',
      'demarque_v2': 'Demarque V2',
      'smowl': 'Smowl',
      'diambra': 'Diambra',
      'edqwest': 'EdQwest',
      'faria': 'Faria',
      'calmedica': 'Calmedica',
      'global': 'Global'
    };
    return labels[tenantId] || tenantId;
  };

  const formerCustomerCount = people.filter(p => p.worked_for_customer === true).length;
  const isFaria = effectiveTenant === 'faria';

  return (
    <Card className="border shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">Quick Edit Manager</CardTitle>
            <CardDescription>
              Edit account scores, tech stack, and persona details
            </CardDescription>
          </div>
          {effectiveTenant && (
            <Badge className="bg-[#00C2C2] text-white text-sm px-3 py-1">
              Editing for: {getTenantLabel(effectiveTenant)}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Tenant-Specific Editing:</strong> Changes will be saved for <strong>{getTenantLabel(effectiveTenant)}</strong> tenant only. 
            {effectiveTenant !== 'instructure' && effectiveTenant !== 'clairelabs' && (
              <span> If the record belongs to another tenant, changes will be stored as tenant overrides.</span>
            )}
          </AlertDescription>
        </Alert>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="accounts">
              <Building2 className="w-4 h-4 mr-2" />
              Accounts
            </TabsTrigger>
            <TabsTrigger value="people">
              <UserIcon className="w-4 h-4 mr-2" />
              People
              {formerCustomerCount > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {formerCustomerCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="space-y-4">
            {/* Account Search */}
            <div className="space-y-2">
              <Label>Search Account</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by account name..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Account Selection */}
            {accountSearch && (
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {isLoadingAccounts ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredAccounts.length > 0 ? (
                  <div className="divide-y">
                    {filteredAccounts.slice(0, 10).map(account => {
                      const tenantOverride = account.tenant_overrides?.[effectiveTenant];
                      const effectiveScore = tenantOverride?.account_fit_score !== undefined
                        ? tenantOverride.account_fit_score
                        : account.account_fit_score;
                      const hasOverride = tenantOverride?.account_fit_score !== undefined;
                      
                      return (
                        <button
                          key={account.id}
                          onClick={() => handleSelectAccount(account)}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                            selectedAccount?.id === account.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {account.logo_url ? (
                                <img src={account.logo_url} alt="" className="w-6 h-6 object-contain rounded flex-shrink-0" />
                              ) : (
                                <Building2 className="w-6 h-6 text-gray-400 flex-shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{account.account_name}</p>
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-gray-500">{account.country}</p>
                                  <Badge variant="outline" className="text-xs">
                                    {account.tenant_id}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            {effectiveScore && (
                              <Badge 
                                variant="secondary" 
                                className={`text-xs flex-shrink-0 ${hasOverride ? 'bg-purple-100 text-purple-800' : ''}`}
                              >
                                {effectiveScore} {hasOverride && '(override)'}
                              </Badge>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 p-4 text-center">No accounts found</p>
                )}
              </div>
            )}

            {/* Account Edit Form */}
            {selectedAccount && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center gap-3">
                  {selectedAccount.logo_url ? (
                    <img src={selectedAccount.logo_url} alt="" className="w-10 h-10 object-contain rounded" />
                  ) : (
                    <Building2 className="w-10 h-10 text-gray-400" />
                  )}
                  <div>
                    <h3 className="font-semibold">{selectedAccount.account_name}</h3>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-500">{selectedAccount.country}</p>
                      <Badge variant="outline" className="text-xs">
                        Primary: {selectedAccount.tenant_id}
                      </Badge>
                      {selectedAccount.tenant_id !== effectiveTenant && (
                        <Badge className="bg-purple-100 text-purple-800 text-xs">
                          Using Override
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fit Score Section */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold text-sm">Account Fit Score</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="account-score">Score (0-100)</Label>
                      <Input
                        id="account-score"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={accountScore}
                        onChange={(e) => setAccountScore(e.target.value)}
                        placeholder="Enter score (e.g., 85)"
                      />
                      {selectedAccount.tenant_id !== effectiveTenant && (
                        <p className="text-xs text-purple-600 font-medium">
                          This will be saved as a tenant override for {getTenantLabel(effectiveTenant)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="account-rationale">Score Rationale</Label>
                      <Textarea
                        id="account-rationale"
                        value={accountRationale}
                        onChange={(e) => setAccountRationale(e.target.value)}
                        placeholder="Explain why this account received this score..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="account-tier">Account Tier</Label>
                      <Select value={accountTier} onValueChange={setAccountTier}>
                        <SelectTrigger id="account-tier">
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Tier 1</SelectItem>
                          <SelectItem value="2">Tier 2</SelectItem>
                          <SelectItem value="3">Tier 3</SelectItem>
                          <SelectItem value="4">Tier 4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Incumbent Sentiment Section */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold text-sm">Incumbent Sentiment</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="incumbent-sentiment">Sentiment</Label>
                      <Input
                        id="incumbent-sentiment"
                        value={incumbentSentiment}
                        onChange={(e) => setIncumbentSentiment(e.target.value)}
                        placeholder="e.g., Satisfied, Frustrated, Exploring alternatives"
                      />
                      {selectedAccount.tenant_id !== effectiveTenant && (
                        <p className="text-xs text-purple-600 font-medium">
                          This will be saved as a tenant override for {getTenantLabel(effectiveTenant)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sentiment-rationale">Sentiment Rationale</Label>
                      <Textarea
                        id="sentiment-rationale"
                        value={sentimentRationale}
                        onChange={(e) => setSentimentRationale(e.target.value)}
                        placeholder="Explain the incumbent sentiment classification..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Target Products Section - only show if has value or user clicked Add */}
                {(showTargetProductsField || targetProducts?.length > 0) ? (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-semibold text-sm">Target Products</h4>
                    <div className="space-y-2">
                      <Label>Products (e.g. SYS, EXAM)</Label>
                      <TargetProductsTagInput
                        value={targetProducts || []}
                        onChange={(val) => {
                          setTargetProducts(val ?? []);
                          if (val?.length === 0) setShowTargetProductsField(false);
                        }}
                        placeholder="Add product..."
                      />
                      <p className="text-xs text-gray-500">
                        Target products for multi-product selling. Stored on account. Save null when cleared.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border-t pt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#00C2C2] hover:bg-[#00C2C2]/10"
                      onClick={() => setShowTargetProductsField(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add target products
                    </Button>
                  </div>
                )}

                {/* Tech Stack Section */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <h4 className="font-semibold text-sm">Tech Stack Products</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="crm-product">CRM Product</Label>
                      <Input
                        id="crm-product"
                        value={crmProduct}
                        onChange={(e) => setCrmProduct(e.target.value)}
                        placeholder="e.g., Salesforce, HubSpot"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="eportfolio-product">ePortfolio Product</Label>
                      <Input
                        id="eportfolio-product"
                        value={eportfolioProduct}
                        onChange={(e) => setEportfolioProduct(e.target.value)}
                        placeholder="e.g., Portfolium, Digication"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="proctoring-product">Proctoring Product</Label>
                      <Input
                        id="proctoring-product"
                        value={proctoringProduct}
                        onChange={(e) => setProctoringProduct(e.target.value)}
                        placeholder="e.g., Proctorio, Respondus"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="library-product">Library Product</Label>
                      <Input
                        id="library-product"
                        value={libraryProduct}
                        onChange={(e) => setLibraryProduct(e.target.value)}
                        placeholder="e.g., Ex Libris, OCLC"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admissions-product">Admissions Product</Label>
                      <Input
                        id="admissions-product"
                        value={admissionsProduct}
                        onChange={(e) => setAdmissionsProduct(e.target.value)}
                        placeholder="e.g., Slate, TargetX"
                      />
                    </div>

                    <div className="space-y-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label htmlFor="home-product" className="cursor-help">
                              Home Product
                            </Label>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">School-to-home management system</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Input
                        id="home-product"
                        value={homeProduct}
                        onChange={(e) => setHomeProduct(e.target.value)}
                        placeholder="e.g., iSAMS, SchoolPod"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Tech stack products are saved to base fields (not tenant-specific)
                  </p>
                </div>

                {/* Faria-Specific Fields */}
                {isFaria && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-semibold text-sm">Faria-Specific Fields</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="faria-products">Faria Products</Label>
                      <Input
                        id="faria-products"
                        value={fariaProducts}
                        onChange={(e) => setFariaProducts(e.target.value)}
                        placeholder="e.g., ManageBac, OpenApply"
                      />
                      <p className="text-xs text-gray-500">
                        Comma-separated list of Faria products
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="renewal-risk">Renewal Risk</Label>
                      <Select value={renewalRisk} onValueChange={setRenewalRisk}>
                        <SelectTrigger id="renewal-risk">
                          <SelectValue placeholder="Select risk level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="risk-rationale">Risk Rationale</Label>
                      <Textarea
                        id="risk-rationale"
                        value={riskRationale}
                        onChange={(e) => setRiskRationale(e.target.value)}
                        placeholder="Explain the renewal risk assessment..."
                        rows={2}
                        className="resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="expansion-product-1">Expansion Product 1</Label>
                        <Input
                          id="expansion-product-1"
                          value={expansionProduct1}
                          onChange={(e) => setExpansionProduct1(e.target.value)}
                          placeholder="Primary expansion opportunity"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="expansion-badge-1">Badge 1</Label>
                          <Input
                            id="expansion-badge-1"
                            value={expansionBadge1}
                            onChange={(e) => setExpansionBadge1(e.target.value)}
                            placeholder="e.g., High Priority"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="expansion-rationale-1">Rationale 1</Label>
                          <Input
                            id="expansion-rationale-1"
                            value={expansionRationale1}
                            onChange={(e) => setExpansionRationale1(e.target.value)}
                            placeholder="Brief rationale"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="expansion-rationale-1-full">Full Rationale 1</Label>
                        <Textarea
                          id="expansion-rationale-1-full"
                          value={expansionRationale1}
                          onChange={(e) => setExpansionRationale1(e.target.value)}
                          placeholder="Full explanation for expansion opportunity 1..."
                          rows={2}
                          className="resize-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="expansion-product-2">Expansion Product 2</Label>
                        <Input
                          id="expansion-product-2"
                          value={expansionProduct2}
                          onChange={(e) => setExpansionProduct2(e.target.value)}
                          placeholder="Secondary expansion opportunity"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="expansion-badge-2">Badge 2</Label>
                          <Input
                            id="expansion-badge-2"
                            value={expansionBadge2}
                            onChange={(e) => setExpansionBadge2(e.target.value)}
                            placeholder="e.g., Medium Priority"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="expansion-rationale-2">Rationale 2</Label>
                          <Input
                            id="expansion-rationale-2"
                            value={expansionRationale2}
                            onChange={(e) => setExpansionRationale2(e.target.value)}
                            placeholder="Brief rationale"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="expansion-rationale-2-full">Full Rationale 2</Label>
                        <Textarea
                          id="expansion-rationale-2-full"
                          value={expansionRationale2}
                          onChange={(e) => setExpansionRationale2(e.target.value)}
                          placeholder="Full explanation for expansion opportunity 2..."
                          rows={2}
                          className="resize-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSaveAccount}
                  disabled={isSavingAccount}
                  className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                >
                  {isSavingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save for {getTenantLabel(effectiveTenant)}
                    </>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="people" className="space-y-4">
            {/* Person Search and Filter */}
            <div className="space-y-2">
              <Label>Search Person</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, account, or role..."
                  value={personSearch}
                  onChange={(e) => setPersonSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="filter-former-customers" 
                  checked={filterFormerCustomers}
                  onCheckedChange={setFilterFormerCustomers}
                />
                <label
                  htmlFor="filter-former-customers"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Show only former customers {formerCustomerCount > 0 && `(${formerCustomerCount})`}
                </label>
              </div>
            </div>

            {/* Person Selection */}
            {personSearch && (
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {isLoadingPeople ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredPeople.length > 0 ? (
                  <div className="divide-y">
                    {filteredPeople.slice(0, 10).map(person => {
                      const tenantOverride = person.tenant_overrides?.[effectiveTenant];
                      const hasOverride = personFields.some(field => 
                        !field.readOnly && tenantOverride?.[field.key] !== undefined
                      );
                      
                      return (
                        <button
                          key={person.id}
                          onClick={() => handleSelectPerson(person)}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                            selectedPerson?.id === person.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {person.profile_photo_url ? (
                              <img src={person.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                                {person.first_name?.[0]}{person.last_name?.[0]}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">
                                  {person.first_name} {person.last_name}
                                </p>
                                {person.worked_for_customer && (
                                  <Badge className="bg-blue-500 text-white text-xs flex-shrink-0">
                                    Former Client
                                  </Badge>
                                )}
                                {hasOverride && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs flex-shrink-0">
                                    override
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{person.role_title}</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-400 truncate">{person.account_name}</p>
                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                  {person.tenant_id}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 p-4 text-center">No people found</p>
                )}
              </div>
            )}

            {/* Person Edit Form */}
            {selectedPerson && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center gap-3">
                  {selectedPerson.profile_photo_url ? (
                    <img src={selectedPerson.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600">
                      {selectedPerson.first_name?.[0]}{selectedPerson.last_name?.[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">
                      {selectedPerson.first_name} {selectedPerson.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">{selectedPerson.role_title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-400">{selectedPerson.account_name}</p>
                      <Badge variant="outline" className="text-xs">
                        Primary: {selectedPerson.tenant_id}
                      </Badge>
                      {selectedPerson.tenant_id !== effectiveTenant && (
                        <Badge className="bg-purple-100 text-purple-800 text-xs">
                          Using Override
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold text-sm">Edit Fields</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {personFields.map(field => {
                      // Determine if this field has a tenant-specific override for the current effectiveTenant
                      const tenantOverrideValue = selectedPerson.tenant_overrides?.[effectiveTenant]?.[field.key];
                      const hasSpecificTenantOverride = tenantOverrideValue !== undefined;
                      const isOverriddenField = selectedPerson.tenant_id !== effectiveTenant && !field.readOnly && (tenantOverrideValue !== undefined || field.key === 'linkedin_url'); // Special check for linkedin_url as it was managed separately before

                      return (
                      <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <Label htmlFor={`person-${field.key}`} className="text-sm">
                          {field.label}
                        </Label>
                        
                        {field.type === 'text' && (
                          <div className="flex items-center gap-2">
                            <Input
                              id={`person-${field.key}`}
                              type="text"
                              value={selectedPerson[field.key] || ''}
                              onChange={(e) => handlePersonFieldChange(field.key, e.target.value)}
                              placeholder={`Enter ${field.label.toLowerCase()}...`}
                              className="mt-1 flex-1"
                              readOnly={field.readOnly}
                            />
                            {field.key === 'linkedin_url' && selectedPerson.linkedin_url && (
                              <Button
                                variant="outline"
                                size="icon"
                                asChild
                                className="mt-1"
                              >
                                <a href={selectedPerson.linkedin_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {field.type === 'number' && (
                          <Input
                            id={`person-${field.key}`}
                            type="number"
                            min={field.min !== undefined ? field.min : 0}
                            max={field.max !== undefined ? field.max : 100}
                            step="0.1"
                            value={selectedPerson[field.key] !== undefined ? selectedPerson[field.key] : ''}
                            onChange={(e) => handlePersonFieldChange(field.key, parseFloat(e.target.value) || (e.target.value === '' ? '' : 0))}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            className="mt-1"
                            readOnly={field.readOnly}
                          />
                        )}
                        
                        {field.type === 'textarea' && (
                          <Textarea
                            id={`person-${field.key}`}
                            value={selectedPerson[field.key] || ''}
                            onChange={(e) => handlePersonFieldChange(field.key, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            rows={3}
                            className="mt-1 resize-none"
                            readOnly={field.readOnly}
                          />
                        )}
                        
                        {field.type === 'select' && (
                          <Select
                            value={selectedPerson[field.key] || ''}
                            onValueChange={(value) => handlePersonFieldChange(field.key, value)}
                            disabled={field.readOnly}
                          >
                            <SelectTrigger id={`person-${field.key}`} className="mt-1">
                              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options.map(option => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        {field.type === 'boolean' && (
                          <div className="flex items-center space-x-2 mt-2">
                            <Checkbox
                              id={`person-${field.key}`}
                              checked={selectedPerson[field.key] || false}
                              onCheckedChange={(checked) => handlePersonFieldChange(field.key, checked)}
                              disabled={field.readOnly}
                            />
                            <label
                              htmlFor={`person-${field.key}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {field.label}
                            </label>
                          </div>
                        )}
                        
                        {field.type === 'date' && (
                          <Input
                            id={`person-${field.key}`}
                            type="date"
                            value={selectedPerson[field.key] || ''}
                            onChange={(e) => handlePersonFieldChange(field.key, e.target.value)}
                            className="mt-1"
                            readOnly={field.readOnly}
                          />
                        )}

                        {field.type === 'product_affinity' && (
                          (() => {
                            const accountForPerson = accounts.find(a =>
                              a.account_id === selectedPerson.account_id ||
                              a.account_name === selectedPerson.account_name
                            );
                            const productOptions = accountForPerson?.target_products || [];
                            if (productOptions.length > 0) {
                              return (
                                <Select
                                  value={selectedPerson[field.key] || '__none__'}
                                  onValueChange={(v) => handlePersonFieldChange(field.key, v === '__none__' ? '' : v)}
                                  disabled={field.readOnly}
                                >
                                  <SelectTrigger id={`person-${field.key}`} className="mt-1">
                                    <SelectValue placeholder="Select product affinity" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">(None)</SelectItem>
                                    {productOptions.map(opt => (
                                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            }
                            return (
                              <Input
                                id={`person-${field.key}`}
                                value={selectedPerson[field.key] || ''}
                                onChange={(e) => handlePersonFieldChange(field.key, e.target.value)}
                                placeholder="Product key"
                                className="mt-1"
                                readOnly={field.readOnly}
                              />
                            );
                          })()
                        )}

                        {isOverriddenField && (field.key !== 'worked_for_customer' && field.key !== 'client_name') && ( // 'worked_for_customer' and 'client_name' are base fields in current save logic
                           <p className="text-xs text-purple-600 font-medium mt-1">
                             This will be saved as a tenant override for {getTenantLabel(effectiveTenant)}
                           </p>
                        )}
                      </div>
                    )})}
                  </div>
                  
                  {selectedPerson.tenant_id !== effectiveTenant && (
                    <p className="text-xs text-purple-600 font-medium">
                      Note: Fields that are not 'worked for customer' or 'client name' will be saved as tenant overrides for {getTenantLabel(effectiveTenant)} when this is not the person's primary tenant.
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSavePerson}
                  disabled={isSavingPerson}
                  className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                >
                  {isSavingPerson ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save for {getTenantLabel(effectiveTenant)}
                    </>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}